import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../chrome/AppShell';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import { Chipbar, MenuItem, MenuLabel, TCard, TFoot, Toolbar, TRow } from '../components/table';
import { Dialog, Field, TextInput } from '../components/forms';
import { PageHeadActions } from '../components/nav';
import { Frow, ListState, useList, useToast } from '../components/roster';
import { dateRangeLabel } from '../lib/jobs';

// No Remove action: the backend has no delete endpoint for contacts.

export default function ContactsV3() {
    const api = useAuthenticatedApi();
    const navigate = useNavigate();
    const toast = useToast();
    const contacts = useList(() => api.get('/production/contacts').then((r: any) => r.data));
    const [query, setQuery] = useState('');
    const [company, setCompany] = useState<string | null>(null);

    const rows = useMemo(() => contacts.rows ?? [], [contacts.rows]);
    const companies = useMemo(
        () => Array.from(new Set(rows.map((c: any) => c.companyName).filter(Boolean))).sort() as string[],
        [rows],
    );
    const q = query.trim().toLowerCase();
    const visible = useMemo(
        () =>
            rows
                .filter((c: any) => !q || [c.name, c.email, c.companyName, c.role].some((x) => (x ?? '').toLowerCase().includes(q)))
                .filter((c: any) => !company || c.companyName === company)
                .sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? '')),
        [rows, q, company],
    );

    const [opened, setOpened] = useState<any>(null);
    /* a late reply for another row (or a closed dialog) is ignored */
    const wanted = useRef<number | null>(null);
    const open = (c: any) => {
        wanted.current = c.id;
        setOpened({ ...c, loading: true });
        api.get(`/production/contacts/${c.id}`)
            .then((r: any) => wanted.current === c.id && setOpened(r.data))
            .catch(() => wanted.current === c.id && setOpened({ ...c, loadFailed: true }));
    };
    const close = () => {
        wanted.current = null;
        setOpened(null);
    };

    const blank = { name: '', companyName: '', role: '', email: '', phone: '' };
    const [adding, setAdding] = useState(false);
    const [f, setF] = useState(blank);
    const [saving, setSaving] = useState(false);
    const emailOk = !f.email.trim() || /^\S+@\S+\.\S+$/.test(f.email.trim());
    const add = async () => {
        if (!f.name.trim() || !emailOk || saving) return;
        setSaving(true);
        try {
            await api.post('/production/contacts', {
                name: f.name.trim(),
                companyName: f.companyName.trim() || null,
                role: f.role.trim() || null,
                email: f.email.trim() || null,
                phone: f.phone.trim() || null,
            });
            setAdding(false);
            toast.show(`Added ${f.name.trim()}.`);
            setF(blank);
            contacts.reload();
        } catch {
            toast.show('Couldn’t add this contact.');
        } finally {
            setSaving(false);
        }
    };

    const [importing, setImporting] = useState(false);
    const [attioId, setAttioId] = useState('');
    const [importingNow, setImportingNow] = useState(false);
    const importAttio = async () => {
        const id = attioId.trim();
        if (!id || importingNow) return;
        setImportingNow(true);
        try {
            const r: any = await api.post('/production/contacts/import-attio', { attioId: id });
            setImporting(false);
            setAttioId('');
            toast.show(`Imported ${r?.data?.name ?? 'contact'} from Attio.`);
            contacts.reload();
        } catch {
            toast.show('Couldn’t find that record in Attio.');
        } finally {
            setImportingNow(false);
        }
    };

    return (
        <AppShell>
            <PageHeadActions
                title="Contacts"
                sub={contacts.rows ? `${rows.length} contacts · ${companies.length} companies` : 'Clients and partners'}
                actions={
                    <>
                        <button className="tb-btn" onClick={() => setImporting(true)}>Import from Attio</button>
                        <button className="tb-new" onClick={() => setAdding(true)}>Add contact</button>
                    </>
                }
            />
            <Toolbar
                searchValue={query}
                onSearch={setQuery}
                searchPlaceholder="Search name, company, or email"
                filterCount={company ? 1 : 0}
                menu={
                    companies.length
                        ? (close) => (
                              <>
                                  <MenuLabel>Company</MenuLabel>
                                  {companies.map((c) => (
                                      <MenuItem key={c} on={company === c} onClick={() => { setCompany(company === c ? null : c); close(); }}>
                                          {c}
                                      </MenuItem>
                                  ))}
                              </>
                          )
                        : undefined
                }
            />
            <Chipbar chips={company ? [{ label: 'Company', value: company, onClear: () => setCompany(null) }] : []} />
            <ListState
                rows={contacts.rows}
                failed={contacts.failed}
                what="contacts"
                reload={contacts.reload}
                empty={visible.length === 0 ? (rows.length === 0 ? 'No contacts yet.' : q ? `Nothing matches “${query}”.` : 'No contacts at this company.') : undefined}
                emptyAction={rows.length === 0 ? 'Add contact' : undefined}
                onEmptyAction={() => setAdding(true)}
            >
                <TCard>
                    {visible.map((c: any, i: number) => {
                        const n = Number(c.activationCount ?? 0);
                        return (
                            <TRow
                                key={c.id}
                                index={i}
                                name={c.name}
                                badge={c.attioId ? 'Attio' : undefined}
                                meta={[c.role, c.email].filter(Boolean).join(' · ')}
                                metaDate={c.companyName ?? ''}
                                date={c.companyName ?? ''}
                                status={`${n} ${n === 1 ? 'event' : 'events'}`}
                                onOpen={() => open(c)}
                                menuItems={[
                                    { label: 'Details', onClick: () => open(c) },
                                    ...(c.email ? [{ label: 'Email', onClick: () => (window.location.href = `mailto:${c.email}`) }] : []),
                                    ...(c.phone ? [{ label: 'Call', onClick: () => (window.location.href = `tel:${c.phone}`) }] : []),
                                ]}
                            />
                        );
                    })}
                </TCard>
            </ListState>
            {contacts.rows && <TFoot>{q || company ? `${visible.length} of ${rows.length} · filtered` : `${rows.length} contacts`}</TFoot>}

            <Dialog
                open={opened !== null}
                title={opened?.name ?? ''}
                onClose={close}
                footer={<button type="button" className="tb-btn" onClick={close}>Close</button>}
            >
                {opened && (
                    <div className="person">
                        <p className="pg-sub" style={{ margin: '0 0 12px' }}>
                            {[opened.role, opened.companyName].filter(Boolean).join(' · ') || 'Contact'}
                        </p>
                        <div className="p-actions">
                            {opened.email && <a className="tb-btn" href={`mailto:${opened.email}`}>Email</a>}
                            {opened.phone && <a className="tb-btn" href={`tel:${opened.phone}`}>Call</a>}
                        </div>
                        <Frow label="Email">{opened.email ?? '—'}</Frow>
                        <Frow label="Phone">{opened.phone ?? '—'}</Frow>
                        <Frow label="Company">{opened.companyName ?? '—'}</Frow>
                        {opened.attioId && <Frow label="Attio">Synced · {opened.attioId}</Frow>}
                        <p className="s-lab" style={{ margin: '14px 0 6px' }}>Events</p>
                        {opened.loading ? (
                            <p className="pg-sub" style={{ margin: 0 }}>Loading…</p>
                        ) : opened.loadFailed ? (
                            <p className="pg-sub" style={{ margin: 0 }}>Couldn’t load events.</p>
                        ) : (opened.activations ?? []).length === 0 ? (
                            <p className="pg-sub" style={{ margin: 0 }}>Not on any events yet.</p>
                        ) : (
                            (opened.activations ?? []).map((a: any) => (
                                <a
                                    className="trow2 linkrow"
                                    key={a.activationId}
                                    href={`/activations/${a.activationId}`}
                                    onClick={(e) => { if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate(`/activations/${a.activationId}`); } }}
                                >
                                    <span className="nm2">
                                        {a.name}
                                        {a.role && <span className="sub2">{a.role}</span>}
                                    </span>
                                    <span className="right">{dateRangeLabel(a.startAt)}</span>
                                </a>
                            ))
                        )}
                    </div>
                )}
            </Dialog>

            <Dialog
                open={adding}
                title="Add contact"
                onClose={() => setAdding(false)}
                onSubmit={add}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setAdding(false)}>Cancel</button>
                        <button type="submit" className="tb-new" disabled={!f.name.trim() || !emailOk || saving}>{saving ? 'Adding…' : 'Add contact'}</button>
                    </>
                }
            >
                <Field label="Name">
                    <TextInput autoFocus autoComplete="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
                </Field>
                <div className="two">
                    <Field label="Company (optional)">
                        <TextInput autoComplete="organization" value={f.companyName} onChange={(e) => setF({ ...f, companyName: e.target.value })} />
                    </Field>
                    <Field label="Role (optional)">
                        <TextInput value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} />
                    </Field>
                </div>
                <Field label="Email (optional)" error={!emailOk ? 'That doesn’t look like an email address.' : undefined}>
                    <TextInput type="email" inputMode="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                </Field>
                <Field label="Phone (optional)">
                    <TextInput type="tel" inputMode="tel" autoComplete="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
                </Field>
            </Dialog>

            <Dialog
                open={importing}
                title="Import from Attio"
                onClose={() => setImporting(false)}
                onSubmit={importAttio}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setImporting(false)}>Cancel</button>
                        <button type="submit" className="tb-new" disabled={!attioId.trim() || importingNow}>{importingNow ? 'Importing…' : 'Import'}</button>
                    </>
                }
            >
                <Field label="Attio record ID" help="From the person’s URL in Attio. Imported contacts stay linked to their record.">
                    <TextInput autoFocus value={attioId} onChange={(e) => setAttioId(e.target.value)} />
                </Field>
            </Dialog>
            {toast.node}
        </AppShell>
    );
}
